import type { SceneObject } from "../../SceneHost";
import {
  buildPathD,
  computePathBBox,
  type PathPoint,
} from "../../pathGeometry";
import {
  getSegmentDisplayPoints,
  isSegmentObject,
} from "../../segmentGeometry";
import {
  applyStrokeDashAttr,
  pathLinecap,
  pathLinejoin,
  pathStrokeColor,
  pathStrokeDash,
  pathStrokeWidth,
  readObjectStyle,
} from "../../../lib/board/sceneStyle";

const SVG_NS = "http://www.w3.org/2000/svg";

function ensureArrowMarker(defs: SVGDefsElement, obj: SceneObject): string {
  const id = `wb-marker-${obj.id}`;
  let marker = defs.querySelector(`#${id}`);
  if (!marker) {
    marker = document.createElementNS(SVG_NS, "marker");
    marker.setAttribute("id", id);
    marker.setAttribute("viewBox", "0 0 10 10");
    marker.setAttribute("refX", "9");
    marker.setAttribute("refY", "5");
    marker.setAttribute("markerWidth", "6");
    marker.setAttribute("markerHeight", "6");
    marker.setAttribute("orient", "auto");
    const head = document.createElementNS(SVG_NS, "path");
    head.setAttribute("d", "M 0 0 L 10 5 L 0 10 z");
    head.setAttribute("fill", "context-stroke");
    marker.appendChild(head);
    defs.appendChild(marker);
  }
  return `url(#${id})`;
}

export function syncSegmentOnGroup(
  g: SVGGElement,
  obj: SceneObject,
  opts?: { defs?: SVGDefsElement; markerEnd?: boolean }
): void {
  if (!isSegmentObject(obj)) return;
  const strokeWidth = pathStrokeWidth(obj);
  const display = getSegmentDisplayPoints(obj);
  const bbox = computePathBBox(display, strokeWidth);
  obj.x = bbox.x;
  obj.y = bbox.y;
  obj.w = bbox.w;
  obj.h = bbox.h;

  g.setAttribute("transform", `translate(${obj.x},${obj.y})`);

  let pathEl = g.querySelector("path");
  if (!pathEl) {
    pathEl = document.createElementNS(SVG_NS, "path");
    pathEl.setAttribute("fill", "none");
    pathEl.setAttribute("pointer-events", "visibleStroke");
    g.appendChild(pathEl);
  }

  pathEl.setAttribute("d", buildPathD(display, obj.x, obj.y));
  pathEl.setAttribute("stroke", pathStrokeColor(obj));
  pathEl.setAttribute("stroke-width", String(strokeWidth));
  pathEl.setAttribute("stroke-linecap", pathLinecap(obj));
  pathEl.setAttribute("stroke-linejoin", pathLinejoin(obj));
  if (pathStrokeDash(obj) !== "wavy") {
    applyStrokeDashAttr(pathEl, pathStrokeDash(obj), strokeWidth);
  } else {
    pathEl.removeAttribute("stroke-dasharray");
  }
  const strokeOpacity = readObjectStyle(obj).strokeOpacity;
  if (typeof strokeOpacity === "number") {
    pathEl.setAttribute("stroke-opacity", String(strokeOpacity));
  } else {
    pathEl.removeAttribute("stroke-opacity");
  }

  if (opts?.markerEnd && opts.defs) {
    pathEl.setAttribute("marker-end", ensureArrowMarker(opts.defs, obj));
  } else {
    pathEl.removeAttribute("marker-end");
  }
}

export function renderSegmentObject(
  obj: SceneObject,
  opts: { type: "line" | "arrow"; defs?: SVGDefsElement }
): SVGGElement | null {
  if (!isSegmentObject(obj) || obj.type !== opts.type) return null;

  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("class", `wb-scene-obj wb-scene-obj--${opts.type}`);
  g.setAttribute("data-object-id", obj.id);

  syncSegmentOnGroup(g, obj, {
    defs: opts.defs,
    markerEnd: opts.type === "arrow",
  });
  return g;
}

export function segmentEndpointsFromObj(obj: SceneObject): [PathPoint, PathPoint] | null {
  if (!isSegmentObject(obj)) return null;
  const a = obj.points[0]!;
  const b = obj.points[1]!;
  return [
    [a[0], a[1]],
    [b[0], b[1]],
  ];
}
